import React from 'react';
import {
  Datagrid,
  DateField,
  FunctionField,
  Pagination,
  ReferenceManyField,
  Show,
  TextField,
  useRecordContext,
  useRedirect
} from 'react-admin';
import {
  Avatar,
  Breadcrumbs,
  Chip,
  Container,
  Grid,
  Link as MuiLink,
  Stack,
  Typography
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { YesNoField } from '../components/YesNoField.tsx';
import ReadOnlyGroupSubgroups from './components/subgroups/ReadOnlyGroupSubgroups.tsx';
import { NoteListSection } from '../components/notes/NoteListSection.tsx';
import { DocumentListSection } from '../components/documents/DocumentListSection.tsx';
import { ConditionalFollowUpDateField } from '../components/ConditionalFollowUpDateField.tsx';
import {
  DetailRow,
  EmptyState,
  RecordEditButton,
  RecordHeader,
  RecordPage,
  RecordSection,
  RecordTabs
} from '../components/record/RecordPage.tsx';

type GroupRecord = {
  id?: number;
  name?: string;
  isPond?: boolean;
  rootIsPond?: boolean;
  parentId?: number | null;
  parent?: { id?: number; name?: string } | null;
};

const GroupOverview = ({ record }: { record: GroupRecord }) => (
  <Grid container spacing={2}>
    <Grid size={{ xs: 12, md: 5 }}>
      <RecordSection
        title="Group details"
        action={<RecordEditButton label="Edit" variant="text" />}
      >
        <Stack spacing={0.25}>
          <DetailRow label="Group ID">
            <Typography variant="body2">{record.id}</Typography>
          </DetailRow>
          <DetailRow label="Pond">
            <YesNoField source={record.parentId ? 'rootIsPond' : 'isPond'} label="Pond" />
          </DetailRow>
          <DetailRow label="Parent group">
            {record.parent?.id && record.parent.name ? (
              <MuiLink component={RouterLink} to={`/groups/${record.parent.id}/show`}>
                {record.parent.name}
              </MuiLink>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Root group
              </Typography>
            )}
          </DetailRow>
        </Stack>
      </RecordSection>
    </Grid>
    <Grid size={{ xs: 12, md: 7 }}>
      <RecordSection
        title="Subgroups"
        description="The hierarchy beneath this group."
        action={<RecordEditButton label="Manage" variant="text" />}
      >
        <ReadOnlyGroupSubgroups />
      </RecordSection>
    </Grid>
  </Grid>
);

const GroupContactsTab = () => {
  const redirect = useRedirect();
  return (
    <RecordSection
      title="Contacts"
      description="Contacts currently associated with this group."
      action={<RecordEditButton label="Manage" variant="outlined" returnTab="contacts" />}
    >
      <ReferenceManyField
        reference="group-contacts"
        target="groupId"
        perPage={25}
        pagination={<Pagination />}
      >
        <Datagrid
          bulkActionButtons={false}
          empty={<EmptyState>No contacts in this group yet.</EmptyState>}
          rowClick={(_id, _resource, row) => {
            const contactId = row?.contactId ?? row?.id;
            if (contactId) redirect('show', 'contacts', contactId);
            return false;
          }}
        >
          <TextField source="displayName" label="Name" />
          <FunctionField
            label="Email"
            render={(row: { primaryEmail?: string }) =>
              row?.primaryEmail ? (
                <a href={`mailto:${row.primaryEmail}`}>{row.primaryEmail}</a>
              ) : (
                '-'
              )
            }
          />
          <FunctionField
            label="Phone"
            render={(row: { primaryPhone?: string }) =>
              row?.primaryPhone ? <a href={`tel:${row.primaryPhone}`}>{row.primaryPhone}</a> : '-'
            }
          />
          <DateField
            source="dateAdded"
            label="Added"
            locales="en-US"
            options={{ timeZone: 'UTC' }}
          />
        </Datagrid>
      </ReferenceManyField>
    </RecordSection>
  );
};

const GroupEventsTab = () => (
  <RecordSection
    title="Events"
    description="Events connected to this group."
    action={<RecordEditButton label="Manage" variant="outlined" returnTab="events" />}
  >
    <ReferenceManyField reference="group-events" target="groupId" perPage={1000}>
      <Datagrid
        bulkActionButtons={false}
        rowClick={false}
        empty={<EmptyState>No events yet.</EmptyState>}
      >
        <TextField source="eventName" label="Event" />
        <TextField source="speaker" label="Speaker" />
        <DateField source="eventDate" label="Date" locales="en-US" options={{ timeZone: 'UTC' }} />
        <TextField source="location" label="Location" />
      </Datagrid>
    </ReferenceManyField>
  </RecordSection>
);

const GroupActivityTab = () => (
  <RecordSection
    title="Activity"
    description="A chronological view of group outreach and follow-up."
    action={<RecordEditButton label="Manage" variant="outlined" returnTab="activity" />}
  >
    <ReferenceManyField reference="group-activities" target="groupId">
      <Datagrid
        bulkActionButtons={false}
        rowClick={false}
        empty={<EmptyState>No activity yet.</EmptyState>}
      >
        <TextField source="type" />
        <TextField source="note" />
        <DateField
          source="dateSent"
          label="Activity date"
          locales="en-US"
          options={{ timeZone: 'UTC' }}
        />
        <ConditionalFollowUpDateField
          source="followUpDate"
          label="Follow up"
          locales="en-US"
          options={{ timeZone: 'UTC' }}
        />
      </Datagrid>
    </ReferenceManyField>
  </RecordSection>
);

const GroupShowContent = () => {
  const record = useRecordContext<GroupRecord>();
  if (!record) return null;

  const isPond = record.parentId ? record.rootIsPond : record.isPond;
  return (
    <RecordPage>
      <RecordHeader
        avatar={
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
            <GroupsOutlinedIcon />
          </Avatar>
        }
        title={record.name?.trim() || 'Group'}
        subtitle={record.parent?.name ? `Subgroup of ${record.parent.name}` : 'Root group'}
        badges={
          <>
            {isPond ? (
              <Chip label="Pond" color="info" size="small" />
            ) : (
              <Chip label="Group" variant="outlined" size="small" />
            )}
            {record.parentId ? (
              <Chip
                icon={<AccountTreeOutlinedIcon />}
                label="Subgroup"
                variant="outlined"
                size="small"
              />
            ) : null}
          </>
        }
        actions={<RecordEditButton />}
      />
      <RecordTabs
        tabs={[
          { value: 'overview', label: 'Overview', content: <GroupOverview record={record} /> },
          { value: 'contacts', label: 'Contacts', content: <GroupContactsTab /> },
          { value: 'events', label: 'Events', content: <GroupEventsTab /> },
          { value: 'activity', label: 'Activity', content: <GroupActivityTab /> },
          {
            value: 'notes',
            label: 'Notes',
            content: (
              <RecordSection
                title="Notes"
                action={
                  <RecordEditButton
                    editPath="notes"
                    label="Manage"
                    variant="outlined"
                    returnTab="notes"
                  />
                }
              >
                <NoteListSection entityType="GROUP" readOnly hideTitle />
              </RecordSection>
            )
          },
          {
            value: 'documents',
            label: 'Documents',
            content: (
              <RecordSection
                title="Documents"
                action={
                  <RecordEditButton
                    editPath="documents"
                    label="Manage"
                    variant="outlined"
                    returnTab="documents"
                  />
                }
              >
                <DocumentListSection entityType="GROUP" readOnly hideTitle />
              </RecordSection>
            )
          }
        ]}
      />
    </RecordPage>
  );
};

const GroupShow: React.FC = () => (
  <Container maxWidth="lg">
    <Breadcrumbs sx={{ mt: 2, mb: 1 }}>
      <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
        Dashboard
      </MuiLink>
      <MuiLink component={RouterLink} underline="hover" color="inherit" to="/groups">
        Groups
      </MuiLink>
      <Typography color="text.primary">View</Typography>
    </Breadcrumbs>
    <Show actions={false} component="div" sx={{ '& .RaShow-noActions': { mt: 0 } }}>
      <GroupShowContent />
    </Show>
  </Container>
);

export default GroupShow;
